import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { ChevronRight, Ruler, Camera, Dumbbell, BookOpen, Check, X, Play } from 'lucide-react'
import { FatSecretIcon } from './FatSecretIcon'
import { MeasurementsSheet } from './modals/MeasurementsSheet'
import { XP_REWARDS } from '@tfa/shared'
import { useAuth } from '../hooks/useAuth'
import { useCurator } from '../contexts/CuratorContext'
import { useTelegram } from '../hooks/useTelegram'
import { OverlayPresence } from './OverlayPresence'
import { api } from '../services/api'
import { getCharacterImage } from '../assets/character'

const F = 'Roboto, sans-serif'
const BG = 'linear-gradient(180deg, #EEF2FF 0%, #EDF4FF 50%, #F0F4F8 100%)'
const PRIMARY = 'linear-gradient(135deg, #6C5CE7 0%, #A29BFE 100%)'

// Видео-инструкция «как делать замеры» — embed-плеер (autoplay+muted+controls),
// встроен прямо в сториз шага «Замеры тела».
const MEASUREMENTS_VIDEO_URL = 'https://v.lpxl.ru/v/qKmFuGbbYEOkHvjDy4uX0A?showTitle=true&showControls=true&muted=true&autoplay=true'
// Инструкция «как правильно делать фото» (статья, не видео).
const PHOTO_GUIDE_URL = 'https://graph.org/Kak-pravilno-delat-fotografii-05-18'
// Фото-отчёт даёт 10 XP (решение владельца; в XP_REWARDS отдельного PHOTO нет).
const PHOTO_XP = 10

const WD = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота']
const MON = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря']
function fmtDate(d: Date): string {
  return `${d.getUTCDate()} ${MON[d.getUTCMonth()]}, ${WD[d.getUTCDay()]}`
}
// Ближайшая пятница от даты старта (длительность тестовой недели).
function nearestFriday(from: Date): Date {
  const d = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate()))
  let i = 0
  while (d.getUTCDay() !== 5 && i < 8) { d.setUTCDate(d.getUTCDate() + 1); i++ }
  return d
}

// Плашка наград: обычный режим vs тестовая неделя (0 XP).
function RewardPlate({ xp, unit }: { xp: number; unit: string }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
      <div style={{ flex: 1, background: 'rgba(108,92,231,0.08)', border: '1.5px solid rgba(108,92,231,0.18)', borderRadius: 14, padding: '10px 12px' }}>
        <div style={{ fontSize: 11, color: '#6C5CE7', fontWeight: 700 }}>Обычный режим</div>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#1C2D41', marginTop: 2 }}>+{xp} XP / {unit}</div>
      </div>
      <div style={{ flex: 1, background: 'rgba(148,163,184,0.12)', border: '1.5px solid rgba(148,163,184,0.25)', borderRadius: 14, padding: '10px 12px' }}>
        <div style={{ fontSize: 11, color: '#64748B', fontWeight: 700 }}>Тестовая неделя</div>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#64748B', marginTop: 2 }}>0 XP</div>
      </div>
    </div>
  )
}

// ── Демо-блок «Сон · Шаги · Вес» для STEP 4: тап → поповер → последовательное
// заполнение всех трёх метрик (≤5 c). Наглядная иллюстрация, без реальных мутаций.
const DEMO_METRICS = [
  { key: 'sleep', emoji: '😴', label: 'Сон', value: '7 ч', color: '#6C5CE7' },
  { key: 'steps', emoji: '👟', label: 'Шаги', value: '8 540', color: '#00B894' },
  { key: 'weight', emoji: '⚖️', label: 'Вес', value: '72.3 кг', color: '#E17055' },
]

function MetricsFillDemo() {
  const [playing, setPlaying] = useState(false)
  const [filled, setFilled] = useState(0)
  const [done, setDone] = useState(false)
  const timers = useRef<number[]>([])
  const clearTimers = useCallback(() => { timers.current.forEach((t) => clearTimeout(t)); timers.current = [] }, [])

  const play = useCallback(() => {
    clearTimers()
    setPlaying(true); setFilled(0); setDone(false)
    timers.current.push(window.setTimeout(() => setFilled(1), 600))
    timers.current.push(window.setTimeout(() => setFilled(2), 1400))
    timers.current.push(window.setTimeout(() => setFilled(3), 2200))
    timers.current.push(window.setTimeout(() => setDone(true), 3000))
    timers.current.push(window.setTimeout(() => { setPlaying(false); setFilled(0); setDone(false) }, 4600))
  }, [clearTimers])

  // Авто-проигрыш один раз при появлении шага + очистка таймеров при размонтировании.
  useEffect(() => { play(); return clearTimers }, [play, clearTimers])

  return (
    <div style={{ position: 'relative', marginTop: 14 }}>
      <button
        onClick={play}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'rgba(108,92,231,0.06)', border: '1.5px solid rgba(108,92,231,0.22)',
          borderRadius: 14, padding: '12px 14px', cursor: 'pointer', fontFamily: F,
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>📋</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#1C2D41' }}>Заполнить день: сон · шаги · вес</span>
        </span>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#6C5CE7', whiteSpace: 'nowrap' }}>Нажми →</span>
      </button>

      {/* Поповер заполнения */}
      <div
        style={{
          position: 'absolute', left: 0, right: 0, top: 0, zIndex: 5,
          transform: playing ? 'translateY(0)' : 'translateY(8px)',
          opacity: playing ? 1 : 0,
          pointerEvents: playing ? 'auto' : 'none',
          transition: 'opacity 0.25s ease, transform 0.25s ease',
          background: '#fff', borderRadius: 16, padding: '14px 14px 12px',
          border: '1px solid rgba(108,92,231,0.18)', boxShadow: '0 14px 44px rgba(26,58,92,0.20)',
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 800, color: '#1C2D41', marginBottom: 8 }}>Заполнение дня</div>
        {DEMO_METRICS.map((m, i) => {
          const isFilled = filled > i
          return (
            <div key={m.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < 2 ? '1px solid rgba(26,58,92,0.06)' : 'none' }}>
              <div style={{ width: 30, height: 30, borderRadius: 9, background: `${m.color}1A`, color: m.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{m.emoji}</div>
              <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: '#1C2D41' }}>{m.label}</span>
              <span style={{ fontSize: 14, fontWeight: 800, color: isFilled ? m.color : '#C7CDD6', transition: 'color 0.3s', minWidth: 60, textAlign: 'right' }}>
                {isFilled ? m.value : '—'}
              </span>
              {isFilled && <Check size={15} color="#34C759" strokeWidth={3} />}
            </div>
          )
        })}
        <div style={{ marginTop: 10, height: 38, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: done ? 'rgba(52,199,89,0.14)' : 'rgba(108,92,231,0.10)', color: done ? '#1E9E4A' : '#6C5CE7', fontWeight: 800, fontSize: 13.5 }}>
          {done ? <><Check size={16} strokeWidth={3} /> Готово · +3 XP</> : 'Заполняем…'}
        </div>
      </div>
    </div>
  )
}

const cta = (enabled = true): React.CSSProperties => ({
  width: '100%', height: 52, borderRadius: 16, border: 'none',
  background: enabled ? PRIMARY : 'rgba(26,58,92,0.1)', color: enabled ? '#fff' : 'rgba(26,58,92,0.3)',
  fontSize: 16, fontWeight: 700, cursor: enabled ? 'pointer' : 'default', fontFamily: F,
  boxShadow: enabled ? '0 6px 24px rgba(108,92,231,0.35)' : 'none',
})
const secondaryCta: React.CSSProperties = {
  width: '100%', height: 48, borderRadius: 16, border: '1.5px solid rgba(108,92,231,0.3)',
  background: 'rgba(255,255,255,0.7)', color: '#6C5CE7', fontSize: 15, fontWeight: 600,
  cursor: 'pointer', fontFamily: F, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
}

/**
 * Объяснялки тестовой недели (STEP 2 — экраны 1–3). Полноэкранный пошаговый
 * flow с «Далее», прогрессом и закрытием. Показывается ОДИН раз при входе в
 * TEST_WEEK (флаг testWeekOnboardingCompleted на Client). Значения XP — из
 * @tfa/shared (XP_REWARDS), не хардкод.
 *
 * NB: STEP 3 (экраны 4–8 + «Готово») будет добавлен следующим заходом; сейчас
 * последний шаг (3) завершает flow и ставит флаг.
 */
export function TestWeekStories() {
  const { user } = useAuth()
  const { isCurator, viewingClientId, isAdminView } = useCurator()
  const { impactHaptic, openLink } = useTelegram()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [step, setStep] = useState(0)
  const [closed, setClosed] = useState(false)
  const [showMeasurements, setShowMeasurements] = useState(false)

  const u = user as Record<string, unknown> | undefined
  const mode = u?.mode as string | undefined
  const product = u?.product as string | undefined
  const seen = !!u?.testWeekOnboardingCompleted
  const firstName = (u?.firstName as string) || 'друг'

  const markSeen = useMutation({
    mutationFn: () => api.markStoriesSeen({ testWeek: true }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['user', 'me'] }),
  })

  // Дата окончания тестовой недели (до ближайшей пятницы от старта).
  const endLabel = useMemo(() => {
    const end = u?.testWeekEndDate ? new Date(u.testWeekEndDate as string)
      : u?.flowStartDate ? nearestFriday(new Date(u.flowStartDate as string)) : null
    return end && !Number.isNaN(end.getTime()) ? fmtDate(end) : null
  }, [u?.testWeekEndDate, u?.flowStartDate])

  // Помечаем «просмотрено» ОДИН раз — при первом действии (Далее/крестик/уход),
  // а не на маунте: иначе одноразовый показ «съедался» при первом же открытии и
  // сторизы переставали появляться вовсе.
  const markedRef = useRef(false)
  const markOnce = useCallback(() => {
    if (!markedRef.current) { markedRef.current = true; markSeen.mutate() }
  }, [markSeen])

  const finish = useCallback(() => {
    impactHaptic('light')
    markOnce()
    setClosed(true)
  }, [impactHaptic, markOnce])

  const next = useCallback(() => { impactHaptic('light'); markOnce(); setStep((s) => s + 1) }, [impactHaptic, markOnce])

  // Уход «сделать задачу» (чат/лекции) — закрываем на сессию + помечаем просмотренным.
  const leaveSession = useCallback((path: string) => { impactHaptic('light'); markOnce(); setClosed(true); navigate(path) }, [impactHaptic, markOnce, navigate])

  // Базовая применимость (без учёта «просмотрено»): клиент, ТФА, TEST_WEEK.
  const baseEligible = !!user && !isCurator && !viewingClientId && !isAdminView
    && mode === 'TEST_WEEK' && product !== 'CLUB'
  // Решение «показать в этой сессии» фиксируем на первом применимом рендере,
  // чтобы фоновое проставление флага (ниже) не схлопнуло уже открытые сторис.
  const showThisSessionRef = useRef<boolean | null>(null)
  if (showThisSessionRef.current === null && baseEligible) showThisSessionRef.current = !seen
  const shouldShow = !closed && baseEligible && showThisSessionRef.current === true

  if (!shouldShow) return null

  const mascot = getCharacterImage(10, u?.gender as never)
  const STEPS = [
    // ── Стр 1 — объяснение тестовой недели ──
    () => (
      <>
        {mascot && <img src={mascot} alt="" style={{ width: 150, height: 200, objectFit: 'contain', display: 'block', margin: '0 auto 16px' }} />}
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#1C2D41', textAlign: 'center', margin: '0 0 10px', letterSpacing: '-0.3px' }}>
          {firstName}, твоя программа ТФА началась!
        </h1>
        <p style={{ fontSize: 16, color: '#1C2D41', textAlign: 'center', margin: '0 0 8px', fontWeight: 600 }}>
          Всё начинается с тестовой недели
        </p>
        {endLabel && (
          <p style={{ fontSize: 14, color: '#6C5CE7', textAlign: 'center', margin: '0 0 14px', fontWeight: 700 }}>
            Длительность до {endLabel}
          </p>
        )}
        <p style={{ fontSize: 14.5, color: '#6B7B8D', textAlign: 'center', lineHeight: 1.6, margin: 0 }}>
          {/* [ТЗ: текст мягкого входа — адаптация, мягкий вход — вставить дословно] */}
          Эта неделя — мягкий вход: спокойно знакомишься с приложением и привычками.
          Очки и TuCoin пока не начисляются — просто пробуешь и осваиваешься.
        </p>
      </>
    ),
    // ── Стр 2 — Шаг 1: FatSecret ──
    () => (
      <>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#6C5CE7', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Шаг 1</div>
        <h2 style={{ fontSize: 21, fontWeight: 800, color: '#1C2D41', margin: '4px 0 12px' }}>Подключи FatSecret</h2>
        <p style={{ fontSize: 14.5, color: '#374151', lineHeight: 1.6, margin: 0 }}>
          FatSecret синхронизирует твоё питание — данные автоматически попадают куратору. Обновление каждый час, итог дня — в 23:59.
        </p>
        <RewardPlate xp={XP_REWARDS.NUTRITION_REPORT.xp} unit="день" />
        <button
          style={{ ...secondaryCta, marginTop: 16 }}
          onClick={async () => {
            try { const { authUrl } = await api.connectFatSecret(); if (authUrl) openLink(authUrl, { external: true }) }
            catch { navigate('/diary') }
          }}
        >
          <FatSecretIcon size={18} /> Подключить FatSecret
        </button>
      </>
    ),
    // ── Стр 3 — Шаг 2: Замеры тела ──
    () => (
      <>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#6C5CE7', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Шаг 2</div>
        <h2 style={{ fontSize: 21, fontWeight: 800, color: '#1C2D41', margin: '4px 0 12px' }}>Замеры тела</h2>
        <p style={{ fontSize: 14.5, color: '#374151', lineHeight: 1.6, margin: 0 }}>
          Каждые выходные, еженедельно с СБ по ПН, нужно вносить замеры тела.
        </p>
        {/* Видео-инструкция — встроена прямо в сториз (autoplay muted, со звуком
            и на весь экран — через контролы плеера). */}
        <div style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: '#6C5CE7', marginBottom: 8 }}>
            <Play size={16} /> Видео-инструкция — как делать замеры
          </div>
          <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', borderRadius: 14, overflow: 'hidden', background: '#000' }}>
            <iframe
              src={MEASUREMENTS_VIDEO_URL}
              title="Видео-инструкция — как делать замеры"
              allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
              allowFullScreen
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
            />
          </div>
        </div>
        <RewardPlate xp={XP_REWARDS.MEASUREMENTS.xp} unit="замер" />
        <button style={{ ...secondaryCta, marginTop: 14 }} onClick={() => { impactHaptic('light'); setShowMeasurements(true) }}>
          <Ruler size={18} /> Внести замеры
        </button>
        <p style={{ fontSize: 12, color: '#9AA5B4', textAlign: 'center', margin: '8px 0 0' }}>Можно внести сейчас или замерить позже.</p>
      </>
    ),
    // ── Стр 4 — Шаг 3: Фото ДО ──
    () => (
      <>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#6C5CE7', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Шаг 3</div>
        <h2 style={{ fontSize: 21, fontWeight: 800, color: '#1C2D41', margin: '4px 0 12px' }}>Фото ДО</h2>
        <p style={{ fontSize: 14.5, color: '#374151', lineHeight: 1.6, margin: 0 }}>
          Каждый месяц будет открываться вкладка, где нужно подгрузить фото в чат с куратором.
        </p>
        <button style={{ ...secondaryCta, marginTop: 14 }} onClick={() => openLink(PHOTO_GUIDE_URL)}>
          <ChevronRight size={18} /> Как правильно делать фото
        </button>
        <RewardPlate xp={PHOTO_XP} unit="отчёт" />
        <button style={{ ...secondaryCta, marginTop: 14 }} onClick={() => leaveSession('/chat')}>
          <Camera size={18} /> Прислать фото
        </button>
      </>
    ),
    // ── Стр 5 — Шаг 4: Сон, шаги, вес ──
    () => (
      <>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#6C5CE7', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Шаг 4</div>
        <h2 style={{ fontSize: 21, fontWeight: 800, color: '#1C2D41', margin: '4px 0 12px' }}>Сон, шаги, вес</h2>
        <p style={{ fontSize: 14.5, color: '#374151', lineHeight: 1.6, margin: 0 }}>
          Каждый день заполняй сон, шаги и вес. Динамику смотри во вкладке «Аналитика».
        </p>
        <MetricsFillDemo />
        <RewardPlate xp={XP_REWARDS.SLEEP.xp + XP_REWARDS.STEPS.xp + XP_REWARDS.WEIGHT.xp} unit="день (сон+шаги+вес)" />
      </>
    ),
    // ── Стр 6 — Шаг 5: Видео-обзор зала ──
    () => (
      <>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#6C5CE7', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Шаг 5</div>
        <h2 style={{ fontSize: 21, fontWeight: 800, color: '#1C2D41', margin: '4px 0 12px' }}>Видео-обзор зала</h2>
        <p style={{ fontSize: 14.5, color: '#374151', lineHeight: 1.6, margin: 0 }}>
          Сними видео-обзор зала, где будешь тренироваться. Если тренируешься дома — пришли список и фото оборудования.
        </p>
        <button style={{ ...secondaryCta, marginTop: 16 }} onClick={() => leaveSession('/chat')}>
          <Dumbbell size={18} /> Отправить куратору
        </button>
      </>
    ),
    // ── Стр 7 — Шаг 6: Фото приёмов пищи ──
    () => (
      <>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#6C5CE7', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Шаг 6</div>
        <h2 style={{ fontSize: 21, fontWeight: 800, color: '#1C2D41', margin: '4px 0 12px' }}>Фото приёмов пищи</h2>
        <p style={{ fontSize: 14.5, color: '#374151', lineHeight: 1.6, margin: 0 }}>
          Присылай фото всех приёмов пищи в конце дня, чтобы куратор понял твои привычки. Это только 7 дней во время тестовой недели.
        </p>
        <div style={{ marginTop: 12, fontSize: 13, color: '#64748B', background: 'rgba(148,163,184,0.12)', border: '1.5px solid rgba(148,163,184,0.25)', borderRadius: 12, padding: '10px 12px' }}>
          В обычном режиме фото еды присылать не нужно.
        </div>
      </>
    ),
    // ── Стр 8 — Шаг 7: Модуль «Быстрый старт» ──
    () => (
      <>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#6C5CE7', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Шаг 7</div>
        <h2 style={{ fontSize: 21, fontWeight: 800, color: '#1C2D41', margin: '4px 0 12px' }}>Модуль «Быстрый старт»</h2>
        <p style={{ fontSize: 14.5, color: '#374151', lineHeight: 1.6, margin: 0 }}>
          Пройди уроки модуля «Быстрый старт» — это база, с которой проще войти в программу.
        </p>
        <RewardPlate xp={XP_REWARDS.LECTURE_WATCHED.xp} unit="урок" />
        <button style={{ ...secondaryCta, marginTop: 14 }} onClick={() => leaveSession('/lectures')}>
          <BookOpen size={18} /> Открыть лекции
        </button>
      </>
    ),
    // ── Стр «Готово!» ──
    () => (
      <>
        <div style={{ width: 72, height: 72, borderRadius: 22, background: PRIMARY, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px', boxShadow: '0 8px 32px rgba(108,92,231,0.4)' }}>
          <Check size={36} color="#fff" strokeWidth={3} />
        </div>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#1C2D41', textAlign: 'center', margin: '0 0 12px' }}>Всё готово!</h1>
        <p style={{ fontSize: 14.5, color: '#374151', textAlign: 'center', lineHeight: 1.6, margin: 0 }}>
          После тестовой недели{endLabel ? ` ${endLabel}` : ''} тебе будут выданы:
        </p>
        <div style={{ marginTop: 12, maxWidth: 300, marginLeft: 'auto', marginRight: 'auto' }}>
          {['План питания по КБЖУ', 'Программа тренировок'].map((t, i) => (
            <div key={t} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{ width: 22, height: 22, borderRadius: 11, background: '#6C5CE7', color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</div>
              <span style={{ fontSize: 14, color: '#1C2D41' }}>{t}</span>
            </div>
          ))}
        </div>
        <p style={{ fontSize: 13.5, color: '#6B7B8D', textAlign: 'center', lineHeight: 1.6, margin: '14px 0 0' }}>
          Ты получишь уведомление в чате по готовности — включи уведомления в боте.
        </p>
      </>
    ),
  ]

  const isLast = step >= STEPS.length - 1

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: BG, fontFamily: F, display: 'flex', flexDirection: 'column' }}>
      <OverlayPresence name="stories" />
      {/* Прогресс + крестик (закрыть). Флаг «просмотрено» уже выставлен при показе. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flex: 1 }}>
          {STEPS.map((_, i) => (
            <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i <= step ? PRIMARY : 'rgba(108,92,231,0.18)' }} />
          ))}
        </div>
        <button
          onClick={finish}
          aria-label="Закрыть"
          style={{ flexShrink: 0, width: 30, height: 30, borderRadius: '50%', border: 'none', background: 'rgba(26,58,92,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
        >
          <X size={17} color="#6B7B8D" />
        </button>
      </div>

      {/* Контент */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 24px 16px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <div>{STEPS[step]()}</div>
      </div>

      {/* Далее / Начать тестовую неделю */}
      <div style={{ padding: '12px 24px', paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))' }}>
        <button style={cta(true)} onClick={isLast ? () => { finish(); navigate('/diary') } : next}>
          {isLast ? 'Начать тестовую неделю' : 'Далее'}
        </button>
      </div>

      {/* Всплывашка замеров поверх сторис (как в полном приложении) */}
      {showMeasurements && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2100 }}>
          <MeasurementsSheet
            open
            onClose={() => setShowMeasurements(false)}
            onSubmitted={() => setShowMeasurements(false)}
          />
        </div>
      )}
    </div>
  )
}
